import {Loader, Stack} from '@mantine/core'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute} from '@tanstack/react-router'

import {getConversionByEngagementLevel} from 'src/api/client/api'
import {ManagerFilter} from 'src/components/analytics/manager-filter'
import SummaryStats, {SummaryStatsExportContent} from 'src/components/analytics/summary-stats'
import {useProspectNavigation} from 'src/components/analytics/use-prospect-navigation'
import {ExportImageButton} from 'src/components/export-image-button'
import {PageHeader} from 'src/components/page-header'
import {useScenarioPlanning} from 'src/components/scenario/scenario-context'

type SearchParams = {manager?: string}

export const Route = createFileRoute(
    '/_authed/organizations/$orgId/conversion-by-engagement-level',
)({
    component: ConversionByEngagementLevelPage,
    validateSearch: (search: Record<string, unknown>): SearchParams => ({
        manager: (search['manager'] as string) || undefined,
    }),
})

function ConversionByEngagementLevelPage() {
    const {orgId} = Route.useParams()
    const {manager} = Route.useSearch()
    const navigateToProspects = useProspectNavigation(orgId)
    const {appliedScenario} = useScenarioPlanning()

    const {data, isLoading, error} = useQuery({
        queryKey: ['page', orgId, 'conversion-by-engagement-level', manager, appliedScenario],
        queryFn: () => getConversionByEngagementLevel(orgId, manager, appliedScenario),
    })

    if (isLoading) {
        return <Loader size="lg" style={{display: 'block', margin: '60px auto'}} />
    }

    if (error) {
        return <div>Error loading page: {(error as Error).message}</div>
    }

    if (!data) return null

    const headerTitle = 'Conversion by Engagement Level'
    const headerDescription = 'Estimated donor conversion rates by engagement level.'

    return (
        <Stack>
            <PageHeader
                title={headerTitle}
                description={headerDescription}
                actions={
                    <ExportImageButton
                        title={headerTitle}
                        renderContent={({kind}) => (
                            <SummaryStatsExportContent data={data} kind={kind} />
                        )}
                        filenameBase="conversion-by-engagement-level"
                    />
                }
            />
            <ManagerFilter orgId={orgId} />
            <SummaryStats
                data={data}
                onProspectFilter={(filters) => navigateToProspects({...filters, manager})}
            />
        </Stack>
    )
}
